import { ApiError, type SavedLogView } from '@central/api';
import { charCount, logLevelOptions, logRangeOptions } from '@central/ui';
import { Alert, Button, Group, Modal, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { useCustomerEnvironments } from '../lib/environment';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import type { LogFilters } from './LogSearch';
import { Empty, QueryState } from './parts';

const MAX_NAME = 80;

/** The search on screen, as a view would save it. */
export interface CurrentSearch {
  customerId: string;
  environmentId: string;
  filters: LogFilters;
}

/**
 * "Saved views" (docs/design/AdminLogs.dc.html): the signed-in user's own
 * named searches. Apply sets the customer, environment and filters and
 * searches; the current search can be saved under a name. A fixed window
 * from a link cannot be saved: views keep a range that ends when applied.
 */
export function SavedLogViewsModal({
  opened,
  onClose,
  current,
  onApply,
}: {
  opened: boolean;
  onClose: () => void;
  current?: CurrentSearch;
  onApply: (v: SavedLogView) => void;
}) {
  const api = useApi();
  const views = useQuery({
    queryKey: keys.logViews,
    queryFn: () => api.logs.views.list(),
    enabled: opened,
  });
  const items = views.data?.items ?? [];
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Saved views"
      size="lg"
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {opened && <SaveForm current={current} />}
        <QueryState isPending={views.isPending} error={views.error}>
          {() =>
            items.length === 0 ? (
              <Empty>No saved views yet.</Empty>
            ) : (
              <ul
                aria-label="Saved views"
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {items.map((v) => (
                  <ViewRow key={v.id} v={v} onApply={() => onApply(v)} />
                ))}
              </ul>
            )
          }
        </QueryState>
      </div>
    </Modal>
  );
}

const rangeLabel = (r: string) => logRangeOptions.find((o) => o.value === r)?.label ?? r;
const levelLabel = (l?: string) => logLevelOptions.find((o) => o.value === (l ?? ''))?.label;

function ViewRow({ v, onApply }: { v: SavedLogView; onApply: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const customers = useQuery({
    queryKey: keys.customerOptions,
    queryFn: () => api.customers.list({ limit: 200 }),
    staleTime: 60_000,
  });
  const envs = useCustomerEnvironments(v.customerId ?? '');
  const remove = useMutation({
    mutationFn: () => api.logs.views.delete(v.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.logViews }),
  });
  const customer = customers.data?.items.find((c) => c.id === v.customerId)?.name;
  const env = envs.data?.find((e) => e.id === v.environmentId)?.name;
  const summary = [
    customer ?? (v.customerId && 'Unknown customer'),
    env ?? (v.environmentId && 'Unknown environment'),
    v.service,
    levelLabel(v.level),
    rangeLabel(v.range),
    v.contains && `contains “${v.contains}”`,
  ].filter(Boolean);
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <b style={{ fontSize: 14 }}>{v.name}</b>
        <span style={{ fontSize: 13, ...muted }}>{summary.join(' · ')}</span>
        {remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
      </div>
      <Group gap={8}>
        <Button size="xs" color="dark" onClick={onApply} aria-label={`Apply ${v.name}`}>
          Apply
        </Button>
        <Button
          size="xs"
          variant="default"
          loading={remove.isPending}
          onClick={() => remove.mutate()}
          aria-label={`Delete ${v.name}`}
        >
          Delete
        </Button>
      </Group>
    </li>
  );
}

function SaveForm({ current }: { current?: CurrentSearch }) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string>();
  const save = useMutation({
    mutationFn: (c: CurrentSearch) =>
      api.logs.views.create({
        name: name.trim(),
        customerId: c.customerId || undefined,
        environmentId: c.environmentId || undefined,
        service: c.filters.service || undefined,
        level: c.filters.level || undefined,
        range: c.filters.range,
        contains: c.filters.contains || undefined,
      }),
    onSuccess: async () => {
      setName('');
      await qc.invalidateQueries({ queryKey: keys.logViews });
    },
  });
  const fixed = current?.filters.range === 'window';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!current || fixed) return;
    const n = name.trim();
    const p = !n
      ? 'Name the view.'
      : charCount(n) > MAX_NAME
        ? `At most ${MAX_NAME} characters.`
        : undefined;
    setProblem(p);
    if (!p && !save.isPending) save.mutate(current);
  };
  const duplicate = save.error instanceof ApiError && save.error.code === 'duplicate';
  return (
    <form onSubmit={submit} noValidate aria-label="Save this search">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <TextInput
          label="Save this search as"
          placeholder="SNMP timeouts, Riverside"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          error={problem ?? (duplicate ? 'You already have a view with this name.' : undefined)}
          disabled={fixed}
          style={{ flex: '1 1 240px' }}
        />
        <Button type="submit" color="dark" loading={save.isPending} disabled={!current || fixed}>
          Save
        </Button>
      </div>
      {fixed && (
        <p style={{ margin: '6px 0 0', fontSize: 13, ...muted }}>
          Choose a time range to save this search; the window from the link is fixed.
        </p>
      )}
      {save.error && !duplicate && (
        <Alert color="red" mt={8}>
          {errorMessage(save.error, {
            too_many_views: 'You have 50 saved views. Delete one first.',
          })}
        </Alert>
      )}
    </form>
  );
}
